import type { AnchorHTMLAttributes, ButtonHTMLAttributes, FC, ReactNode } from "react";

import classnames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";
import { Icon } from "@jti/core-components/components/Icon";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";

import styles from "./HeaderToolsItem.module.scss";

type HeaderToolsItemBaseProps = {
  /** Icon or other content to render inside the item */
  children?: ReactNode;
  /** Responsive icon: renders md on desktop, sm on mobile. When provided, children are ignored. */
  icon?: DirectionalIconComponent;
};

export type HeaderToolsItemLinkProps = HeaderToolsItemBaseProps & {
  href: string;
  nativeBehaviour?: boolean;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children">;

export type HeaderToolsItemButtonProps = HeaderToolsItemBaseProps & {
  href?: never;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

export type HeaderToolsItemProps = HeaderToolsItemLinkProps | HeaderToolsItemButtonProps;

const HeaderToolsItemContent: FC<Pick<HeaderToolsItemBaseProps, "children" | "icon">> = ({ children, icon }) => {
  if (icon) {
    return (
      <>
        <span className={styles.iconDesktop}>
          <Icon icon={icon} size="md" />
        </span>
        <span className={styles.iconMobile}>
          <Icon icon={icon} size="sm" />
        </span>
      </>
    );
  }
  return <>{children}</>;
};

export const HeaderToolsItem: FC<HeaderToolsItemProps> = (props) => {
  const { children, icon } = props;

  if (typeof props.href === "string") {
    const {
      href,
      nativeBehaviour,
      children: _children,
      icon: _icon,
      className,
      ...linkProps
    } = props as HeaderToolsItemLinkProps;

    return (
      <LinkBehaviour
        {...linkProps}
        href={href}
        nativeBehaviour={nativeBehaviour}
        className={classnames(styles.item, className)}
      >
        <HeaderToolsItemContent icon={icon}>{children}</HeaderToolsItemContent>
      </LinkBehaviour>
    );
  }

  const {
    children: _children,
    icon: _icon,
    className,
    type = "button",
    ...buttonProps
  } = props as HeaderToolsItemButtonProps;

  return (
    <button {...buttonProps} type={type} className={classnames(styles.item, className)}>
      <HeaderToolsItemContent icon={icon}>{children}</HeaderToolsItemContent>
    </button>
  );
};
